import React from "react";
import { Link } from "react-router-dom";
import { PaziaBadge } from "components/atoms";
import {
  RECONCILIATION_ISSUE_SEVERITY,
  RECONCILIATION_PAYMENT_STATUS,
  ReconciliationIssueApi,
  ReconciliationStudentApi,
} from "api/course-finances";
import { routes } from "consts";
import {
  dateTime,
  issueCodeLabel,
  money,
  paymentSourceLabel,
  paymentStatusLabel,
  signedMoney,
  trancheStatusLabel,
} from "./reconciliationLabels";

// ThemeColors has no red, so error badges use their own classes (same shape as PaziaBadge size="xs").
const errorBadgeClasses =
  "inline-flex items-center justify-center truncate rounded-full border border-rose-200 bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-700 shadow-sm dark:border-rose-500/40 dark:bg-rose-500/20 dark:text-rose-300";

const thClasses = "px-2 py-1 text-left font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap";
const tdClasses = "px-2 py-1 whitespace-nowrap";
const flaggedCellClasses = "border-l-4 border-l-rose-500";

const issueRef = (issue: ReconciliationIssueApi) =>
  [
    issue.paymentId != null ? `płatność #${issue.paymentId}` : null,
    issue.courseTrancheId != null ? `transza #${issue.courseTrancheId}` : null,
  ]
    .filter(Boolean)
    .join(", ");

export const IssueBadge = ({ issue }: { issue: ReconciliationIssueApi }) => {
  const text = `${issueCodeLabel(issue.code)}${issue.impact != null ? ` ${signedMoney(issue.impact)}` : ""}`;
  return (
    <span title={issueRef(issue) || undefined}>
      {issue.severity === RECONCILIATION_ISSUE_SEVERITY.ERROR ? (
        <span className={errorBadgeClasses}>{text}</span>
      ) : (
        <PaziaBadge color="amber" size="xs">
          {text}
        </PaziaBadge>
      )}
    </span>
  );
};

const HeaderValue = ({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) => (
  <span className="whitespace-nowrap text-sm">
    <span className="text-slate-500 dark:text-slate-400">{label}: </span>
    <span className={`font-semibold ${className ?? "text-slate-800 dark:text-slate-100"}`}>{value}</span>
  </span>
);

const ids = (values: number[]) => (values.length ? values.map((id) => `#${id}`).join(", ") : "—");

const redText = "text-rose-600 dark:text-rose-400";

/** One student: header line with the amounts, issue badges, their tranches and their payments. */
export const StudentReconciliationCard = ({
  student,
  isRemoved = false,
}: {
  student: ReconciliationStudentApi;
  isRemoved?: boolean;
}) => {
  const flaggedPaymentIds = new Set(student.issues.map((i) => i.paymentId).filter((id): id is number => id != null));
  const flaggedTrancheIds = new Set(
    student.issues.map((i) => i.courseTrancheId).filter((id): id is number => id != null),
  );

  return (
    <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <Link
          className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
          to={routes.STUDENT(student.userId)}
        >
          {student.firstName} {student.surname}
        </Link>
        <HeaderValue label="kod rabatowy" value={student.discountCode || "—"} />
        <HeaderValue
          label="cena"
          value={
            student.originalPrice !== student.price ? (
              <>
                <span className="mr-1 font-normal text-slate-400 line-through">{money(student.originalPrice)}</span>
                {money(student.price)}
              </>
            ) : (
              money(student.price)
            )
          }
        />
        <HeaderValue label="należne" value={money(student.dueForPaidTranches)} />
        <HeaderValue label="wpłacone" value={money(student.countedRevenue)} />
        <HeaderValue label="pozostało" value={money(student.remaining)} />
        <HeaderValue
          label="różnica"
          value={signedMoney(student.difference)}
          className={student.difference !== 0 ? redText : undefined}
        />
        {(isRemoved || student.retainedRevenue != null) && (
          <HeaderValue label="zatrzymany przychód" value={money(student.retainedRevenue ?? 0)} />
        )}
        {student.unexplained !== 0 && (
          <span className={`whitespace-nowrap text-sm font-semibold ${redText}`}>
            niewyjaśnione: {money(student.unexplained)}
          </span>
        )}
      </div>

      {student.issues.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {student.issues.map((issue, i) => (
            <IssueBadge key={i} issue={issue} />
          ))}
        </div>
      )}

      <div className="mt-3 overflow-x-auto">
        <div className="mb-1 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Transze</div>
        {student.tranches.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Brak</div>
        ) : (
          <table className="w-full text-sm dark:text-slate-300">
            <thead className="border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className={thClasses}>Nazwa</th>
                <th className={thClasses}>Kwota</th>
                <th className={thClasses}>Po rabacie</th>
                <th className={thClasses}>Status</th>
                <th className={thClasses}>Termin</th>
                <th className={thClasses}>Płatności</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {student.tranches.map((t) => (
                <tr key={t.courseTrancheId}>
                  <td className={`${tdClasses} ${flaggedTrancheIds.has(t.courseTrancheId) ? flaggedCellClasses : ""}`}>
                    {t.name || `Transza #${t.courseTrancheId}`}
                  </td>
                  <td className={tdClasses}>{money(t.amount)}</td>
                  <td className={tdClasses}>{money(t.discountedAmount)}</td>
                  <td className={tdClasses}>{trancheStatusLabel(t.status)}</td>
                  <td className={tdClasses}>{dateTime(t.dueDate)}</td>
                  <td className={tdClasses}>{ids(t.paymentIds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-3 overflow-x-auto">
        <div className="mb-1 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Płatności</div>
        {student.payments.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Brak</div>
        ) : (
          <table className="w-full text-sm dark:text-slate-300">
            <thead className="border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className={thClasses}>#Id</th>
                <th className={thClasses}>Źródło</th>
                <th className={thClasses}>Kwota zapisana</th>
                <th className={thClasses}>Kwota liczona</th>
                <th className={thClasses}>Status</th>
                <th className={thClasses}>Utworzono</th>
                <th className={thClasses}>Zapłacono</th>
                <th className={thClasses}>Transze</th>
                <th className={thClasses}>Faktura</th>
                <th className={thClasses}>Komentarz</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {student.payments.map((p) => (
                <tr
                  key={p.paymentId}
                  className={p.status !== RECONCILIATION_PAYMENT_STATUS.SUCCESS ? "opacity-50" : undefined}
                >
                  <td className={`${tdClasses} ${flaggedPaymentIds.has(p.paymentId) ? flaggedCellClasses : ""}`}>
                    #{p.paymentId}
                  </td>
                  <td className={tdClasses}>{paymentSourceLabel(p.source)}</td>
                  <td className={tdClasses}>{p.rawAmount}</td>
                  <td className={tdClasses}>{money(p.countedAmount)}</td>
                  <td className={tdClasses}>{paymentStatusLabel(p.status)}</td>
                  <td className={tdClasses}>{dateTime(p.createdDate)}</td>
                  <td className={tdClasses}>{dateTime(p.paymentDate)}</td>
                  <td className={tdClasses}>
                    {ids(
                      student.tranches.some((t) => t.paymentIds.includes(p.paymentId))
                        ? student.tranches
                            .filter((t) => t.paymentIds.includes(p.paymentId))
                            .map((t) => t.courseTrancheId)
                        : p.courseTrancheIds,
                    )}
                  </td>
                  <td className={tdClasses}>{p.invoiceId != null ? `#${p.invoiceId}` : "—"}</td>
                  <td className="px-2 py-1">{p.comment || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
